// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/enterprise-admin/organization/GuidedMultiSelect.jsx

import React, { useId, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Field,
  TextInput,
} from "../../../design-system/components";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

export default function GuidedMultiSelect({
  label,
  value = [],
  options = [],
  onChange,
  placeholder = "Choose options",
  allowCustom = true,
  helper,
}) {
  const generatedId = useId().replace(/:/g, "");
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");

  const selected = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : []),
    [value]
  );

  const toggle = (option) => {
    const exists = selected.some(
      (item) => normalize(item) === normalize(option)
    );

    onChange(
      exists
        ? selected.filter(
            (item) => normalize(item) !== normalize(option)
          )
        : [...selected, option]
    );
  };

  const addCustom = () => {
    const next = customValue.trim();
    if (
      !next ||
      selected.some((item) => normalize(item) === normalize(next))
    ) {
      return;
    }

    onChange([...selected, next]);
    setCustomValue("");
  };

  return (
    <Field
      label={label}
      help={helper}
      className="oasys-guided-select"
      id={`guided-select-${generatedId}`}
    >
      {({ id, "aria-describedby": ariaDescribedBy }) => (
        <>
          <button
            id={id}
            type="button"
            className="oasys-guided-select__trigger"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-controls={`${id}-menu`}
            aria-describedby={ariaDescribedBy}
          >
            <span>
              {selected.length
                ? `${selected.length} selected`
                : placeholder}
            </span>
            <span aria-hidden="true">{open ? "⌃" : "⌄"}</span>
          </button>

          {selected.length ? (
            <div
              className="oasys-guided-select__chips"
              aria-label={`${label} selections`}
            >
              {selected.map((item) => (
                <button
                  key={item}
                  type="button"
                  className="oasys-guided-select__chip"
                  onClick={() => toggle(item)}
                  aria-label={`Remove ${item}`}
                >
                  {item}
                  <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          ) : null}

          {open ? (
            <div
              id={`${id}-menu`}
              className="oasys-guided-select__menu"
            >
              <div
                className="oasys-guided-select__options"
                role="group"
                aria-label={`${label} options`}
              >
                {options.map((option) => {
                  const checked = selected.some(
                    (item) => normalize(item) === normalize(option)
                  );

                  return (
                    <Checkbox
                      key={option}
                      label={option}
                      checked={checked}
                      onChange={() => toggle(option)}
                    />
                  );
                })}
              </div>

              {allowCustom ? (
                <div className="oasys-guided-select__custom">
                  <TextInput
                    value={customValue}
                    onChange={(event) =>
                      setCustomValue(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addCustom();
                      }
                    }}
                    placeholder="Add another option"
                    aria-label={`Add custom ${label}`}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addCustom}
                    disabled={!customValue.trim()}
                  >
                    Add
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Field>
  );
}
